import { useMemo } from 'react';

import { mixedDentitionMap } from '@/modules/insuranceExport/constants';
import { TOTAL_TEETH } from '@/modules/patientList/patientStrip/patientStripOdontogram/hooks';
import type { TDisplayOrderQuadrantAbbreviations } from '@/modules/patientList/patientStrip/patientStripOdontogram/types';
import { getMatchingPrimaryTooth } from '@/modules/patientList/patientStrip/patientStripOdontogram/utils';
import type { TVisitSummary } from '@/react-query/useVisitSummaries';
import type { ValidUniversalToothId } from '@videa/utils';

import type { ImageObject } from '../types';

// This hook filters out the permanent teeth that are present in the visit
// summary but have a matching primary tooth which should be prioritized.
export const useFilteredVisitSummaryTeeth = (visitSummary?: TVisitSummary) => {
  const permanentToothIds = Array.from({ length: TOTAL_TEETH }).map(
    (_, index) => String(index + 1),
  );

  return useMemo(() => {
    const defaultTeeth = [] as TVisitSummary['teeth'];
    return permanentToothIds.reduce((acc, _, index) => {
      const permanentToothId = String(index + 1);
      const permanentTooth = visitSummary?.teeth?.find(
        ({ toothId }) => toothId === permanentToothId,
      );
      const primaryTooth = getMatchingPrimaryTooth(
        permanentToothId as ValidUniversalToothId,
        visitSummary,
      );

      const tooth = primaryTooth ?? permanentTooth;
      if (tooth) {
        acc.push(tooth);
      }

      return acc;
    }, defaultTeeth);
  }, [permanentToothIds, visitSummary]);
};

export const useVisitSummaryTeethByQuadrants = (
  teeth?: TVisitSummary['teeth'],
) => {
  return useMemo(
    () =>
      (teeth ?? []).reduce(
        (acc, tooth) => {
          const mixedDentitionEl = mixedDentitionMap.find(
            (el) =>
              el.permanentTooth === tooth.toothId ||
              el.primaryTooth === tooth.toothId,
          );
          if (mixedDentitionEl) {
            const index = mixedDentitionMap.indexOf(mixedDentitionEl);
            if (index <= 7) {
              acc.UR = [...acc.UR, tooth];
            } else if (index <= 15) {
              acc.UL = [...acc.UL, tooth];
            } else if (index <= 23) {
              acc.LL = [...acc.LL, tooth];
            } else {
              acc.LR = [...acc.LR, tooth];
            }
          }

          return acc;
        },
        {
          UR: [],
          UL: [],
          LR: [],
          LL: [],
        } as Record<TDisplayOrderQuadrantAbbreviations, TVisitSummary['teeth']>,
      ),
    [teeth],
  );
};

export const getQuadrantByToothNumber = (
  toothId: ValidUniversalToothId,
): TDisplayOrderQuadrantAbbreviations | null => {
  const mixedDentitionEl = mixedDentitionMap.find(
    (el) => el.permanentTooth === toothId || el.primaryTooth === toothId,
  );
  if (!mixedDentitionEl) return null;

  const index = mixedDentitionMap.indexOf(mixedDentitionEl);
  if (index <= 7) {
    return 'UR';
  } else if (index <= 15) {
    return 'UL';
  } else if (index <= 23) {
    return 'LL';
  } else {
    return 'LR';
  }
};

// This function is used to filter teeth based on the active image and selected
// teeth. It's used in the visit sidebar to show information for relevant teeth.
export const getTeethFilteredByImageView = (
  teeth: TVisitSummary['teeth'] | undefined,
  activeImage: ImageObject | undefined,
  selectedTeeth: string[],
  isPrintout?: boolean,
) => {
  return (teeth || []).filter((tooth) => {
    const findingsToothIds =
      activeImage?.models?.tooth_identification?.findings.map(
        ({ toothNumber }) => String(toothNumber),
      );

    if (isPrintout && selectedTeeth.length) {
      return (
        findingsToothIds?.includes(tooth.toothId) &&
        selectedTeeth.includes(tooth.toothId)
      );
    }

    // SINGLE IMAGE VIEW
    // In the visit sidebar, it's possible that we came to single image view
    // from tooth selection view, in which case there is both an activeImage and
    // selectedTeeth. In this case, we should show the visit sidebar for single
    // image view.
    if (activeImage) {
      return findingsToothIds?.includes(tooth.toothId);
    }

    // TOOTH SELECTION VIEW
    if (selectedTeeth.length) {
      return selectedTeeth.includes(tooth.toothId);
    }

    // FMX VIEW
    return true;
  });
};

export const getVisitSidebarView = (
  activeImage?: ImageObject,
  selectedTeeth?: string[],
) => {
  if (activeImage) return 'SINGLE_IMAGE';
  if (selectedTeeth?.length) return 'TOOTH_SELECTION';
  return 'FMX';
};

export const getToothHasBoneLevelMeasurements = (
  tooth: TVisitSummary['teeth'][number],
) => {
  return (
    typeof tooth.distBwAblDistalToCejDistal === 'number' ||
    typeof tooth.distBwAblMesialToCejMesial === 'number' ||
    typeof tooth.paDistalBoneLevel === 'number' ||
    typeof tooth.paMesialBoneLevel === 'number'
  );
};
